// 批次 19（B8-1，Gitea issue #14）：dashboard 頁型的整頁 body（crud-list 頁 opt-in）。
//
// 掛在 PageBehavior.dashboardBody（本批新增 hook；complexBody／reportBody 同款 opt-in 先例），
// CrudListPage 早退（批次 16/17 同款）。契約 0 欄（contracts/pages/main-*.json）——本頁非契約
// 驅動，結構/行為以 legacy JSP＋JS 為基準（執行文件 §1/§2）。
//
// S1（main-student-main）行為（studentMain.js 全量；其他 student 變體於 S3/S4 重用）：
//   - menuRow 點擊（bindEvents :36-172）：todoForm→queryMyTodoFormList＋modal；studyRecord→
//     `training/studyRecord/status`（帶 planInst 四參＋defaultFinishStatus=NOT）；exam→
//     `exam/testing`（＋testingStatus=0）；availableCourses→`tsghfunc/homepageClassQuery`；
//     leaveForm→`leave/leaveForm`；mentorInterview→planFormList(2086)；moodThermometer→
//     planFormList(2085)；calendarBox→calendarModal。
//   - 公告（:56-68）：點主旨→announcementModal（getAnnouncementAjax）＋makeAnnouncementAsReadedAjax
//     ＋未讀 span 移除。
//   - 訓練資訊（:174-262）：coreSkill→queryCoreSkillForm＋modal（無五年級計畫 alert :160-168）；
//     planForm→planFormList(＋defaultPhaseDeptId)；courseForm→classFormList(＋defaultDeptId/
//     startDate/endDate)；GPA→單表單 showFillForm（**第三參數重複帶 formTemplateId＝legacy
//     quirk 照實**：studentMain.js:259，showFillForm 本身不用該參數）／多表單 gpaFormModal；
//     EPA→epaFormModal。
//   - showFillForm（mainUtils.js:606-616）：window.open `form/prompt/fillForm?isWindowOpen=true&
//     fromPage=main&formKey=…&refTable=…&refId=…&formTargetId=…`——雛形以站內導航呈現
//     （D19-x 登記：window.open 不鏡像）。
//   - 行事曆事件（modal/calendar.jsp:71-86 eventClick）：schedule→getMyScheduleAjax＋
//     addCalendarModal（修改個人行程）；class|meeting→CourseModal（批次 11 CourseViewModal）。
//   - 載入提醒（studentMain.jsp:39-46）：discussBoardSize>0 → alert（連結 discuss/discussBoard）。
//   - scrollToCurrentPhase（studentMain.js:187-197）：目前階段橫向捲動至可見。
//
// openUrl 帶參不落地：雛形目標頁暫不消費 query 參數（無跨頁深鏈先例），D19-x 登記。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from '@/components/base/Link';
import { Table, type TableColumn, type TableRow } from '@/components/base/Table';
import { ContentPanel, ModalFrame, ProcessingOverlay, useDialogs } from '@/components/layouts';
import {
  dashboardGate,
  readDashboardData,
  updateDashboardData,
  type DashboardData,
  type DashboardMaPlanForm,
  type DashboardPhase,
  type DashboardStudentListItem,
  type DashboardTodoForm,
  type StudentMainDto,
  type TeacherStudentDetail,
} from '@/mocks/dashboard-store';
import { useScenario, useSession } from '@/mocks/provider';
import { CourseViewModal } from '@/pages/course/CourseViewModal';
import { dashboardVariant } from './dashboardConfig';
import {
  AnnouncementPanel,
  CalendarMenuBox,
  GradeLights,
  PhaseBox,
  PhaseItem,
  StudentMenuBox,
  TeacherMenuBox,
  TodoMenuBox,
  boxHeightPercent,
  kebab,
  tradeText,
} from './dashboardParts';
import {
  AnnounceModal,
  CoreSkillFormsModal,
  EpaFormsModal,
  GpaFormsModal,
  PlanFormsModal,
  ScheduleModal,
  StudentEpaGradeModal,
  StudentGpaGradeModal,
  TodoFormModal,
  type GpaFormsModalProps,
} from './dashboardModals';
import { LightCalendar } from './LightCalendar';

/** menuBox 待辦 key → 圖片／文案／legacy class（studentMain.js:225-274 renderMenu 的開關表）。 */
const TODO_MENU_BOXES: Readonly<
  Record<
    string,
    { readonly imgSrc: string; readonly menuText: string; readonly boxClass: string }
  >
> = {
  ALL_FORM: { imgSrc: 'img/main/待填表單.png', menuText: '待填表單', boxClass: 'todoForm clickable' },
  STUDY_RECORD: { imgSrc: 'img/main/學習紀錄.png', menuText: '學習紀錄', boxClass: 'studyRecord clickable' },
  EXAM: { imgSrc: 'img/main/線上測驗.png', menuText: '線上測驗', boxClass: 'exam clickable' },
  AVAILABLE_COURSES: { imgSrc: 'img/main/可報名課程.png', menuText: '可報名課程', boxClass: 'availableCourses clickable' },
  LEAVE_FORM: { imgSrc: 'img/main/請假單.png', menuText: '請假單', boxClass: 'leaveForm clickable' },
  MENTOR_INTERVIEW: { imgSrc: 'img/main/導生面談紀錄.png', menuText: '導生面談紀錄', boxClass: 'mentorInterview clickable' },
  MOOD_THERMOMETER: { imgSrc: 'img/main/心情溫度計.png', menuText: '心情溫度計', boxClass: 'moodThermometer clickable' },
};

/** 訓練資訊欄位 → uiId 鍵（data-ui-id 一律 kebab-case，CJK 不入 id；§10.1）。 */
const TRAINING_COLUMN_UI_KEYS: Readonly<Record<string, string>> = {
  訓練階段: 'phase',
  計畫表單: 'plan-form',
  課程表單: 'course-form',
  階段成績: 'phase-score',
  等第制: 'gpa',
  EPA: 'epa',
  EPAs: 'epa',
};

/** 行事曆盒的未來行程過濾（studentMain.js:309 `start > now && eventType != 'planInst'`）。 */
export function futureSchedules(events: DashboardData['calendarEvents'], now: Date) {
  return events.filter((event) => {
    const startDate = new Date(event.start);
    return startDate.getTime() - now.getTime() > 0 && event.eventType !== 'planInst';
  });
}

/** 等第制盒顯示值（mainUtils.js:284-310 getGPAFormBox：gradeList join "/"，空＝"-"）。 */
export function gpaDisplayText(phase: DashboardPhase): string {
  const gradeList = phase.gpaForms.map((form) => form.displayGrade);
  if (gradeList.length === 0) {
    gradeList.push('-');
  }
  return gradeList.join('/');
}

/** 等第制盒完成度（mainUtils.js:290-297：doneCnt/total×100）。 */
export function gpaCompletePercent(phase: DashboardPhase): number {
  const total = phase.gpaForms.length;
  if (total === 0) return 0;
  const doneCnt = phase.gpaForms.filter((form) => form.isDone).length;
  return (doneCnt / total) * 100;
}

export function DashboardBody({ pageId, variantPageId }: { pageId: string; variantPageId?: string }): ReactElement {
  // 批次 20：main-main 角色分流（R20-1）以 variantPageId 指定變體（鏡像 controller 以別頁 JSP 渲染
  // 同一 URL）；ui-id 一律以路由 pageId 為前綴（同一 route 語意）。
  const config = dashboardVariant(variantPageId ?? pageId);
  if (config === undefined) {
    throw new Error(`DashboardBody：pageId ${pageId} 未登錄 DASHBOARD_VARIANTS`);
  }
  const scenario = useScenario();
  const dialogs = useDialogs();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [announceId, setAnnounceId] = useState<string | null>(null);
  const [todoOpen, setTodoOpen] = useState(false);
  const [coreskillOpen, setCoreskillOpen] = useState(false);
  const [gpaOpen, setGpaOpen] = useState<readonly DashboardPhase['gpaForms'][number][] | null>(null);
  const [epaOpen, setEpaOpen] = useState<readonly DashboardPhase['epaForms'][number][] | null>(null);
  /** S4（MA）：計畫表單 modal（planFormsForMA；planForm 盒點擊開啟）。 */
  const [planFormsOpen, setPlanFormsOpen] = useState<readonly DashboardMaPlanForm[] | null>(null);
  const [scheduleEditing, setScheduleEditing] = useState<DashboardData['mySchedules'][number] | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [courseRow, setCourseRow] = useState<DashboardData['courseRows'][number] | null>(null);
  /** S2（teacher）：已開啟的學員資訊面板（prepend 語意；同學員重開＝移除舊面板）。 */
  const [openStudentPanels, setOpenStudentPanels] = useState<readonly TeacherStudentDetail[]>([]);
  const [gpaGradeOpen, setGpaGradeOpen] = useState<TeacherStudentDetail | null>(null);
  const [epaGradeOpen, setEpaGradeOpen] = useState<TeacherStudentDetail | null>(null);
  const now = new Date();
  const discussAlertShown = useRef(false);
  const phaseRowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        // scenario 閘門（MockDataSource.gate 同語意；dashboard-store.dashboardGate）。
        dashboardGate(scenario);
        // 批次 20：淺合併（舊持久層缺 deptAdmin 欄位時以種子補齊；readDashboardData 內含首載寫回）。
        const { data: merged } = readDashboardData(scenario, now);
        const next = merged;
        if (alive) {
          setData(next);
          // 首頁載入提醒（studentMain.jsp:39-46）：discussBoardSize>0 → alert（整句是連結）。
          if (!discussAlertShown.current && next.discussBoardSize > 0) {
            discussAlertShown.current = true;
            void dialogs.alert(
              <Link href="/discuss/discussBoard?from=main" uiId={`${pageId}-discuss-board-link`}>
                {`您有${next.discussBoardSize}則留言,請於留言版確認.`}
              </Link>,
            );
          }
        }
      } catch (error) {
        if (alive) setErrorText(error instanceof Error ? error.message : '查詢失敗');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // now／dialogs 由 render 常數取得（同一次載入語意），不進依賴。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario]);

  // scrollToCurrentPhase（studentMain.js:187-197：目前階段橫向捲至可見；legacy animate 1 秒
  // scrollLeft，雛形同參數以 scrollLeft 直接定位——jsdom 無 scrollIntoView）。
  useEffect(() => {
    const row = phaseRowRef.current;
    if (data === null || row === null) return;
    const current = row.querySelector('[data-is-current="true"]') as HTMLElement | null;
    if (current !== null) {
      const first = row.querySelector('li') as HTMLElement | null;
      row.scrollLeft = (current.offsetLeft ?? 0) - (first?.offsetLeft ?? 0);
    }
    // 資料載入後執行一次。
  }, [data === null]);

  if (loading) {
    return <ProcessingOverlay uiId={`${pageId}-loading`} open />;
  }
  if (errorText !== null || data === null) {
    // legacy getStudentData 的 error handler 只 console.log（mainUtils.js:437-440）——
    // 雛形以 alert 呈現錯誤（比照其他 body 的 MockDataError 處理），文案＝閘門訊息。
    return (
      <div data-ui-id={`${pageId}-error`} className="p-4">
        {errorText ?? '查詢失敗'}
      </div>
    );
  }

  const dto: StudentMainDto | null = data.studentMainDto[config.family];
  const schedules = futureSchedules(data.calendarEvents, now);
  const { session } = useSession();
  /** openUrl 鏡像：站內導航＋query 參數（D19-x：目標頁暫不消費參數）。 */
  const openUrl = (path: string, params: Record<string, string | number | boolean> = {}) => {
    const search = new URLSearchParams(
      Object.entries(params).map(([key, value]) => [key, String(value)]),
    ).toString();
    navigate(search === '' ? path : `${path}?${search}`);
  };

  /**
   * 面談紀錄的表單樣板 id 開關（dentisit 版：dentisit/studentMain.js:113-126／dentisit
   * teacherMain.js:66-83 的 jobTypeIdName switch：牙醫UGY→3050／牙醫PGY→3027／住院醫師→
   * 學員 2265·教師 2263【不同！】；ugy/ma＝固定 2086（studentMain.js:135-151）；
   * default → showAlertDialog('資料有誤')＝null。
   */
  const mentorInterviewFormTemplateId = (family: string, jobTypeIdName: string | undefined): number | null => {
    if (family !== 'dentisit') {
      return 2086;
    }
    switch (jobTypeIdName) {
      case '牙醫UGY':
        return 3050;
      case '牙醫PGY':
        return 3027;
      case '住院醫師':
        return config.perspective === 'teacher' ? 2263 : 2265;
      default:
        return null;
    }
  };

  /** showFillForm（mainUtils.js:606-616；window.open 鏡像為站內導航——D19-x）。 */
  const showFillForm = (formKey: string, refTable: string, refId: string, formTargetId: string) => {
    openUrl('/form/prompt/fillForm', {
      isWindowOpen: 'true',
      fromPage: 'main',
      formKey,
      refTable,
      refId,
      formTargetId,
    });
  };


  // ── S5：資訊面版子頁（newMainExamList.jsp/newMainStudyRecord.jsp；breadcrumb1＝${informationPanelVar}）──
  if (pageId === 'main-new-main-exam-list') {
    // commonMyExamList.jsp:10-52：學員第 4 欄＝應完成日；教師/管理員＝學員姓名（契約表頭＝
    // admin 擷取：測驗目標/試卷名稱/出題教師/學員姓名）。試卷名稱連結 openTestingPage＝
    // window.open('exam/testing/prompt/viewTesting?isWindowOpen=true&testingId=…','viewTesting')
    // ——雛形以站內導航鏡像（D19-x）。
    const isStudentRole = session.role === 'student';
    const examColumns: readonly TableColumn[] = [
      { key: 'examTargetName', header: '測驗目標', align: 'center' },
      { key: 'examName', header: '試卷名稱', align: 'center' },
      { key: 'teacherName', header: '出題教師', align: 'center' },
      isStudentRole
        ? { key: 'finishDateStr', header: '應完成日', align: 'center' }
        : { key: 'studentName', header: '學員姓名', align: 'center' },
    ];
    return (
      <div data-ui-id={pageId}>
        <Table
          uiId={`${pageId}-todo-exam-table`}
          columns={examColumns}
          rows={data.todoExams as unknown as readonly TableRow[]}
          rowKey={(row) => `${pageId}-todo-exam-${kebab(String(row['id']))}`}
          renderCell={(row, columnKey) => {
            if (columnKey === 'examName') {
              return (
                <a
                  href="#"
                  data-ui-id={`${pageId}-todo-exam-link-${kebab(String(row['id']))}`}
                  className="cursor-pointer underline"
                  onClick={(event) => {
                    event.preventDefault();
                    openUrl('/exam/testing/prompt/viewTesting', {
                      isWindowOpen: 'true',
                      testingId: String(row['id']),
                    });
                  }}
                >
                  {String(row['examName'])}
                </a>
              );
            }
            const value = row[columnKey];
            return value === null || value === undefined ? '' : String(value);
          }}
        />
      </div>
    );
  }
  if (pageId === 'main-new-main-study-record') {
    // commonMyStudyRecordList.jsp:18-49：訓練計畫/訓練階段（genPhaseName）/學員（連結）/
    // 學習項目（studyRecordList ul li）；學員連結 → studyRecordFormModalVar.showFill——
    // 雛形以站內導航到 /training/studyRecord/fill 鏡像（D19-x：legacy 開 modal）。
    const studyColumns: readonly TableColumn[] = [
      { key: 'planTargetName', header: '訓練計畫', align: 'center' },
      { key: 'phaseText', header: '訓練階段', align: 'center' },
      { key: 'studentName', header: '學員', align: 'center' },
      { key: 'studyItems', header: '學習項目', align: 'left' },
    ];
    const rows = data.todoStudyRecords.map((row) => ({
      ...row,
      // ePortfolio.genPhaseName(row,true) 逐行鏡像（ePortfolio.js:2022-2032）：phaseName 起、
      // subPhaseName 非空才加 '/'、groupName 非空加前綴 'G/'（空段保留 legacy 的尾斜線形）。
      phaseText: (() => {
        let result = row.phaseName ?? '';
        if (result.length > 0 && (row.subPhaseName ?? '') !== '') {
          result += '/';
        }
        result += row.subPhaseName ?? '';
        if ((row.groupName ?? '') !== '') {
          result = `${row.groupName}/${result}`;
        }
        return result;
      })(),
      _id: row.id,
    }));
    return (
      <div data-ui-id={pageId}>
        <Table
          uiId={`${pageId}-todo-study-record-table`}
          columns={studyColumns}
          rows={rows as unknown as readonly TableRow[]}
          rowKey={(row) => `${pageId}-todo-study-record-${kebab(String(row['_id']))}`}
          renderCell={(row, columnKey) => {
            if (columnKey === 'studentName') {
              return (
                <a
                  href="#"
                  data-ui-id={`${pageId}-todo-study-record-link-${kebab(String(row['_id']))}`}
                  className="showFill cursor-pointer underline"
                  onClick={(event) => {
                    event.preventDefault();
                    openUrl('/training/studyRecord/fill', {
                      phaseTargetId: String(row['phaseTargetId']),
                      formKey: String(row['formKey']),
                      studyRecordFormId: String(row['studyRecordFormId']),
                    });
                  }}
                >
                  {String(row['studentName'])}
                </a>
              );
            }
            if (columnKey === 'studyItems') {
              const record = row as unknown as DashboardData['todoStudyRecords'][number];
              return (
                <ul data-ui-id={`${pageId}-todo-study-record-items-${kebab(String(row['_id']))}`} className="ml-4 list-disc">
                  {record.studyRecordList.map((item, index) => (
                    <li key={index}>{item.studyItemName}</li>
                  ))}
                </ul>
              );
            }
            const value = row[columnKey];
            return value === null || value === undefined ? '' : String(value);
          }}
        />
      </div>
    );
  }


  /** 公告點擊（studentMain.js:56-68：showAnnounce＋makeAnnouncementAsReadedAjax＋未讀移除）。 */
  const openAnnouncement = (id: string) => {
    setAnnounceId(id);
    const row = data.announcements.find((announcement) => announcement.id === id);
    if (row !== undefined && row.announcementId === undefined) {
      setData(
        updateDashboardData(
          scenario,
          (current) => ({
            ...current,
            announcements: current.announcements.map((announcement) =>
              announcement.id === id ? { ...announcement, announcementId: announcement.id } : announcement,
            ),
          }),
          now,
        ),
      );
    }
  };

  /** 待填表單列點擊（modal/todoForm.jsp:90-110 分流）。 */
  const openTodoForm = (row: DashboardTodoForm) => {
    if (row.kind === 'leave') {
      openUrl('/leave/leaveForm', { doQueryFlag: 1, todo: 'true' });
      return;
    }
    if (row.refTable === 'CoEditForm') {
      // coEditForm/queryPage：雛形無此 route（routes.generated 無 coEditForm 查詢頁）→
      // 導向站內「未實作」頁（誠實呈現）。
      openUrl('/_/not-implemented');
      return;
    }
    if (row.refTable === 'studyRecord') {
      openUrl('/training/studyRecord/openStudyRecordList');
      return;
    }
    if (row.refTable === 'examList') {
      openUrl('/exam/testing/openExamList');
      return;
    }
    showFillForm(row.formKey, row.refTable, row.refId, row.formTargetId);
  };

  /** 行事曆事件點擊（modal/calendar.jsp:71-86 eventClick）。 */
  const onCalendarEventClick = (event: DashboardData['calendarEvents'][number]) => {
    if (event.eventType === 'schedule') {
      // 個人行程 → getMyScheduleAjax（種子明細）→ addCalendarModal（修改個人行程）。
      const detail = data.mySchedules.find((schedule) => schedule.id === event.id) ?? {
        id: event.id,
        content: event.title,
        scheduleStartDateString: event.start.slice(0, 16).replace('T', ' '),
        scheduleEndDateString: event.start.slice(0, 16).replace('T', ' '),
        memo: '',
        groupKey: '',
      };
      setScheduleEditing(detail);
      setScheduleModalOpen(true);
      return;
    }
    if (event.eventType === 'class' || event.eventType === 'meeting') {
      // class|meeting → CourseModal（批次 11 CourseViewModal；種子課程列）。
      const row = data.courseRows.find((candidate) => String(candidate['id']) === event.id);
      if (row === undefined) {
        void dialogs.alert('查無課程資料');
        return;
      }
      setCourseRow(row);
    }
  };

  /**
   * S2（teacher）：點學員（teacherMain.js:94-105）→ getStudentData 鏡像（store 詳情）→
   * 學員資訊面板（prepend＋同學員舊面板移除，renderStudentPanel :241-250）。
   */
  const openStudentPanel = (student: DashboardStudentListItem, teacherType: 'MENTOR' | 'CLINICAL') => {
    const detail = data.studentDetails[config.family].find(
      (candidate) => candidate.planInst.studentId === student.id,
    );
    if (detail === undefined) {
      void dialogs.alert('查無學員資料');
      return;
    }
    setOpenStudentPanels((current) => [
      { ...detail, teacherType },
      ...current.filter((panel) => panel.planInst.studentId !== student.id),
    ]);
  };

  /** 教師視角 menuRow 學員名單盒的資料（teacherMain.js:314-341 renderStudents）。 */
  const teacherDto = config.perspective === 'teacher' ? data.teacherMainDto[config.family] : null;

  return (
    <div data-ui-id={pageId}>
      {/* menuRow（studentMain.jsp:58-62 div.menuRow；studentMain.js renderMenu/renderCalendarMenu；
          teacher 變體＝teacherMain.js renderMenu/renderStudents） */}
      <div data-ui-id={`${pageId}-menu-row`} className="mb-4 flex flex-wrap gap-3">
        {config.perspective === 'student' && dto !== null
          ? (Object.keys(dto.todoItemMap) as (keyof StudentMainDto['todoItemMap'])[]).map((key) => {
              const spec = TODO_MENU_BOXES[key];
              if (spec === undefined) {
                return null; // legacy default: 不產盒（studentMain.js:271-273）
              }
              const planParams = dto.planInst;
              const onClick =
                key === 'ALL_FORM'
                  ? () => setTodoOpen(true) // studentMain.js:143-147（queryMyTodoFormList＝種子讀取）
                  : key === 'STUDY_RECORD'
                    ? () =>
                        openUrl('/training/studyRecord/status', {
                          defaultAnnualId: planParams.annualId,
                          defaultOrganizationId: planParams.organizationId,
                          defaultJobTypeId: planParams.jobTypeId,
                          defaultPlanTargetId: planParams.planTargetId,
                          defaultFinishStatus: 'NOT',
                          doQueryFlag: 1,
                        }) // studentMain.js:84-97
                    : key === 'EXAM'
                      ? () =>
                          openUrl('/exam/testing', {
                            defaultAnnualId: planParams.annualId,
                            defaultOrganizationId: planParams.organizationId,
                            defaultJobTypeId: planParams.jobTypeId,
                            defaultPlanTargetId: planParams.planTargetId,
                            testingStatus: 0,
                            doQueryFlag: 1,
                          }) // studentMain.js:99-113
                      : key === 'AVAILABLE_COURSES'
                        ? () => openUrl('/tsghfunc/homepageClassQuery') // studentMain.js:115-118
                        : key === 'LEAVE_FORM'
                          ? config.family === 'ma'
                            ? undefined
                            : // MA 版無 div.leaveForm click handler（medicalAffairs/studentMain.js bindEvents
                              // 無此分支）——盒照渲染但點擊無反應＝legacy 照實。
                              () =>
                                openUrl('/leave/leaveForm', {
                                  defaultAnnualId: planParams.annualId,
                                  defaultOrganizationId: planParams.organizationId,
                                  defaultJobTypeId: planParams.jobTypeId,
                                  defaultPlanTargetId: planParams.planTargetId,
                                  doQueryFlag: 1,
                                }) // studentMain.js:120-133
                          : key === 'MENTOR_INTERVIEW'
                            ? () => {
                                if (config.family === 'ma') {
                                  // MA：getFormTemplateListAjax（purpose:'plan'）找 導生面談紀錄表
                                  // （medicalAffairs/studentMain.js:100-136）；找不到＝-1 照帶。
                                  const found = data.maFormTemplates.find((item) => item.name === '導生面談紀錄表');
                                  openUrl('/form/planFormList', {
                                    defaultAnnualId: planParams.annualId,
                                    defaultOrganizationId: planParams.organizationId,
                                    defaultJobTypeId: planParams.jobTypeId,
                                    defaultPlanTargetId: planParams.planTargetId,
                                    defaultFormDeptId: -1,
                                    defaultFormTemplateId: found?.id ?? '-1',
                                    doQueryFlag: 1,
                                    defaultFormStatus: 'false',
                                    showMyForm: 'true',
                                  });
                                  return;
                                }
                                const formTemplateId = mentorInterviewFormTemplateId(
                                  config.family,
                                  dto.planInst.jobTypeIdName,
                                );
                                if (formTemplateId === null) {
                                  // dentisit/studentMain.js:122-124 default：showAlertDialog('資料有誤')。
                                  void dialogs.alert('資料有誤');
                                  return;
                                }
                                openUrl('/form/planFormList', {
                                  defaultAnnualId: planParams.annualId,
                                  defaultOrganizationId: planParams.organizationId,
                                  defaultJobTypeId: planParams.jobTypeId,
                                  defaultPlanTargetId: planParams.planTargetId,
                                  defaultFormDeptId: -1,
                                  defaultFormTemplateId: formTemplateId,
                                  doQueryFlag: 1,
                                  defaultFormStatus: 'false',
                                  showMyForm: 'true',
                                });
                              } // studentMain.js:135-151；dentisit 版＝dentisit/studentMain.js:113-133
                            : key === 'MOOD_THERMOMETER'
                              ? () =>
                                  openUrl('/form/planFormList', {
                                    defaultAnnualId: planParams.annualId,
                                    defaultOrganizationId: planParams.organizationId,
                                    defaultJobTypeId: planParams.jobTypeId,
                                    defaultPlanTargetId: planParams.planTargetId,
                                    defaultFormDeptId: -1,
                                    // dentisit 版心情溫度計＝2092（dentisit/studentMain.js:150）；
                                    // 其餘＝2085（studentMain.js:153-169）。
                                    defaultFormTemplateId: config.family === 'dentisit' ? 2092 : 2085,
                                    doQueryFlag: 1,
                                    defaultFormStatus: 'false',
                                    showMyForm: 'true',
                                  }) // studentMain.js:153-169
                              : undefined;
              return (
                <TodoMenuBox
                  key={key}
                  pageId={pageId}
                  boxClass={spec.boxClass}
                  imgSrc={spec.imgSrc}
                  menuText={spec.menuText}
                  displayText={dto.todoItemMap[key]?.incompleteQuantity ?? 0}
                  onClick={onClick}
                />
              );
            })
          : null}
        {config.perspective === 'student' && dto !== null ? (
          <>
            <TeacherMenuBox
              pageId={pageId}
              type="mentors"
              imgSrc="img/main/導師.png"
              menuText="導師"
              teachers={dto.mentors}
            />
            <TeacherMenuBox
              pageId={pageId}
              type="clinicalTeachers"
              imgSrc="img/main/實習單位臨床教師.png"
              menuText="實習單位臨床教師"
              teachers={dto.clinicalTeachers}
            />
          </>
        ) : null}
        {config.calendarMenuBox ? (
          <CalendarMenuBox pageId={pageId} now={now} schedules={schedules} onClick={() => setCalendarOpen(true)} />
        ) : null}
        {config.perspective === 'teacher' && teacherDto !== null
          ? (Object.keys(teacherDto.todoItemMap) as ('ALL_FORM' | 'MENTOR_INTERVIEW')[]).map((key) => {
              const spec = TODO_MENU_BOXES[key];
              if (spec === undefined) {
                return null;
              }
              return (
                <TodoMenuBox
                  key={key}
                  pageId={pageId}
                  boxClass={spec.boxClass}
                  imgSrc={spec.imgSrc}
                  menuText={spec.menuText}
                  displayText={teacherDto.todoItemMap[key]?.incompleteQuantity ?? 0}
                  onClick={
                    key === 'ALL_FORM'
                      ? () => setTodoOpen(true) // teacherMain.js:61-65
                      : () => {
                          // 導生面談紀錄（teacherMain.js:74-88：annualId/planTargetId＝-1；
                          // dentisit 版 formTemplateId＝jobTypeIdName switch（dentisit/teacherMain.js:66-83）；
                          // MA 版＝getFormTemplateListAjax 鏡像（medicalAffairs/teacherMain.js:67-95；
                          // legacy TeacherMain 為 renderTeacherPage 內的隱含全域 :279，雛形以種子查找等價）。
                          let formTemplateId: number | string;
                          if (config.family === 'ma') {
                            const found = data.maFormTemplates.find((item) => item.name === '導生面談紀錄表');
                            formTemplateId = found?.id ?? '-1';
                          } else {
                            const switched = mentorInterviewFormTemplateId(config.family, teacherDto.jobTypeIdName);
                            if (switched === null) {
                              void dialogs.alert('資料有誤');
                              return;
                            }
                            formTemplateId = switched;
                          }
                          openUrl('/form/planFormList', {
                            defaultAnnualId: -1,
                            defaultPlanTargetId: -1,
                            defaultFormDeptId: -1,
                            defaultFormTemplateId: formTemplateId,
                            defaultFormStatus: 'false',
                            showMyForm: 'true',
                            doQueryFlag: 1,
                          });
                        }
                  }
                />
              );
            })
          : null}
        {config.perspective === 'teacher' && teacherDto !== null ? (
          <>
        {config.perspective === 'teacher' && teacherDto !== null ? (
          <>
            {/* 導生名單（zwy menu_Box_student_Tmpl：C1/C2 鈕＋依年級過濾；dentisit menu_Box_student_M_Tmpl：
                無鈕、全列；teacherMain.js:314-341 vs dentisit/teacherMain.js:335-341） */}
            <StudentMenuBox
              pageId={pageId}
              teacherType="MENTOR"
              imgSrc="img/main/導生名單.png"
              menuText="導生名單"
              showTimeToggle={false}
              showGradeToggle={config.family === 'ugy'}
              filterByGrade={config.family === 'ugy'}
              currentStudents={teacherDto.mentorStudents}
              pastStudents={[]}
              onSelectStudent={(student, _time, _grade) => openStudentPanel(student, 'MENTOR')}
            />
            {/* 指導學生名單（zwy：過/現＋C1/C2；dentisit dentisit_Menu_Box_student_C_Tmpl：僅過/現、
                全列；teacherMain.js:325-341 vs dentisit/teacherMain.js:343-360） */}
            <StudentMenuBox
              pageId={pageId}
              teacherType="CLINICAL"
              imgSrc="img/main/指導學生名單.png"
              menuText="指導學生名單"
              showTimeToggle={true}
              showGradeToggle={config.family === 'ugy'}
              filterByGrade={config.family === 'ugy'}
              currentStudents={teacherDto.clinicalStudents}
              pastStudents={teacherDto.pastClinicalStudents}
              onSelectStudent={(student, _time, _grade) => openStudentPanel(student, 'CLINICAL')}
            />
          </>
        ) : null}
          </>
        ) : null}
      </div>

      {/* 公告欄（studentMain.jsp:60-77；More/Less 點擊＝mainUtils announcesLimit=3） */}
      <AnnouncementPanel
        pageId={pageId}
        announcements={data.announcements}
        onOpenAnnouncement={openAnnouncement}
      />

      {/* 訓練資訊 panel（student 變體限定；teacher 變體是 studentsRow＝S2） */}
      {config.perspective === 'student' && dto !== null && config.trainingColumns.length > 0 ? (
        <ContentPanel uiId={`${pageId}-student-panel`} title="訓練資訊">
          {/* coreSkillRow（studentMain.jsp:87；renderStudentCoreSkills mainUtils.js:206-224；
              dentisit 頁無此區（renderDentisitStudentPlanPanel 不產 coreSkill）——以「等第制」欄
              存在與否（＝ugy 家族）判斷；點擊＝studentMain.js:160-168＋showCoreSkillForm :255-262） */}
          {dto.coreSkillDTO !== undefined && config.trainingColumns.includes('等第制') ? (
            <div data-ui-id={`${pageId}-coreskill-row`} className="mb-3 flex gap-1 overflow-x-auto">
              <div
                data-ui-id={`${pageId}-box-coreskill-total`}
                className="flex h-[120px] w-[90px] flex-col items-center justify-start border border-border bg-[#f2f2f2] p-1"
                title={`height=${boxHeightPercent(0)}%（legacy 總盒 height:0，mainUtils.js:212-215）`}
              >
                <span className="text-center text-[12px]">
                  臨床核心技能
                  <br />
                  {dto.coreSkillDTO.completedQuantity}/{dto.coreSkillDTO.totalQuantity}
                </span>
              </div>
              {dto.coreSkillDTO.deptCoreSkills.map((deptCoreSkill, index) => (
                <PhaseBox
                  key={deptCoreSkill.expansion1}
                  pageId={pageId}
                  kind="coreSkill"
                  index={index}
                  target={{
                    completePercent: deptCoreSkill.completePercent,
                    totalQuantity: deptCoreSkill.totalQuantity,
                    displayText: `${deptCoreSkill.displayText}<br>${deptCoreSkill.completedQuantity}/${deptCoreSkill.totalQuantity}`,
                  }}
                  clickable
                  onClick={() => {
                    if (dto.fifthPlanTargetIdList.length === 0) {
                      // studentMain.js:166 showAlertDialog('無五年級計畫！')。
                      void dialogs.alert('無五年級計畫！');
                      return;
                    }
                    setCoreskillOpen(true); // queryCoreSkillForm（種子讀取）＋showCoreSkillFormsModal
                  }}
                />
              ))}
            </div>
          ) : null}
          {/* planInstRow（titleRow＋boxs-row；欄位組由 config.trainingColumns 驅動） */}
          <div data-ui-id={`${pageId}-planinst-row`}>
            <div data-ui-id={`${pageId}-title-row`} className="flex text-center text-[13px] font-bold">
              {config.trainingColumns.map((column) => (
                <div key={column} data-ui-id={`${pageId}-title-${TRAINING_COLUMN_UI_KEYS[column]}`} className="flex-1 p-1">
                  {column}
                </div>
              ))}
            </div>
            <div data-ui-id={`${pageId}-boxs-row`} className="flex overflow-x-auto">
              {/* 訓練階段（phase_Tmpl 橫向進度條；scrollToCurrentPhase） */}
              {config.trainingColumns.includes('訓練階段') ? (
                <div ref={phaseRowRef} data-ui-id={`${pageId}-phase-row`} className="flex">
                  <ul className="flex list-none p-0">
                    {dto.phaseList.map((phase, index) => (
                      <PhaseItem
                        key={phase.phaseTargetId}
                        pageId={pageId}
                        index={index}
                        phase={phase}
                        // dentisit/MA 版 substage/ma 模板：進度條內顯示 tradeText(phaseTargetName)
                        // （mainUtils.js:155/:187）；ugy 版＝phaseDeptName（phase_Tmpl:229-243）。
                        displayName={config.family === 'ugy' ? undefined : tradeText(phase.phaseTargetName)}
                      />
                    ))}
                  </ul>
                </div>
              ) : null}
              {/* 計畫表單（planFormRow；studentMain.js:174-191 click → planFormList＋defaultPhaseDeptId；
                  MA 版＝showPlanFormsModal（medicalAffairs/studentMain.js:171-175）） */}
              {config.trainingColumns.includes('計畫表單') ? (
                <div data-ui-id={`${pageId}-planform-row`} className="flex">
                  {dto.phaseList.map((phase, index) => (
                    <PhaseBox
                      key={phase.phaseTargetId}
                      pageId={pageId}
                      kind="planForm"
                      index={index}
                      target={phase.planForm}
                      clickable={phase.planForm.totalQuantity > 0}
                      onClick={() => {
                        if (config.family === 'ma') {
                          setPlanFormsOpen(phase.planForms ?? []);
                          return;
                        }
                        openUrl('/form/planFormList', {
                          defaultAnnualId: dto.planInst.annualId,
                          defaultOrganizationId: dto.planInst.organizationId,
                          defaultJobTypeId: dto.planInst.jobTypeId,
                          defaultPlanTargetId: dto.planInst.planTargetId,
                          defaultPhaseDeptId: phase.phaseDeptId,
                          doQueryFlag: 1,
                          // dentisit 版（SubPhase 模板）加帶起訖＋defaultPhaseType
                          // （dentisit/studentMain.js:180-182）。
                          ...(config.family === 'dentisit'
                            ? {
                                defaultStartDate: phase.startDate,
                                defaultEndDate: phase.endDate,
                                defaultPhaseType: 'SubPhase',
                              }
                            : {}),
                          });
                        }
                      }
                    />
                  ))}
                </div>
              ) : null}
              {/* 課程表單（courseFormRow；studentMain.js:193-213 click → classFormList） */}
              {config.trainingColumns.includes('課程表單') ? (
                <div data-ui-id={`${pageId}-courseform-row`} className="flex">
                  {dto.phaseList.map((phase, index) => (
                    <PhaseBox
                      key={phase.phaseTargetId}
                      pageId={pageId}
                      kind="classForm"
                      index={index}
                      target={phase.classForm}
                      clickable={phase.classForm.totalQuantity > 0}
                      onClick={() =>
                        openUrl('/form/classFormList', {
                          defaultOrganizationId: dto.planInst.organizationId,
                          defaultJobTypeId: dto.planInst.jobTypeId,
                          defaultDeptId: phase.phaseDeptId,
                          defaultStartDate: phase.startDate,
                          defaultEndDate: phase.endDate,
                          doQueryFlag: 1,
                          // dentisit 版加 defaultPhaseType（dentisit/studentMain.js:199）。
                          ...(config.family === 'dentisit' ? { defaultPhaseType: 'SubPhase' } : {}),
                        })
                      }
                    />
                  ))}
                </div>
              ) : null}
              {/* 階段成績（phaseScoreRow；MA——getPhaseScoreBox mainUtils.js:262-276；
                  phaseScoreRow click → classFormList（medicalAffairs/studentMain.js:179-192）） */}
              {config.trainingColumns.includes('階段成績') ? (
                <div data-ui-id={`${pageId}-phasescore-row`} className="flex">
                  {dto.phaseList.map((phase, index) => {
                    const score = (phase as { phaseScore?: { score: string } }).phaseScore?.score ?? '-';
                    return (
                      <PhaseBox
                        key={phase.phaseTargetId}
                        pageId={pageId}
                        kind="phaseScore"
                        index={index}
                        target={{
                          completePercent: score === '-' ? 0 : 100,
                          totalQuantity: score === '-' ? 0 : 1,
                          displayText: score,
                        }}
                        clickable={score !== '-'}
                        onClick={() =>
                          openUrl('/form/classFormList', {
                            defaultOrganizationId: dto.planInst.organizationId,
                            defaultJobTypeId: dto.planInst.jobTypeId,
                            defaultDeptId: phase.phaseDeptId,
                            defaultStartDate: phase.startDate,
                            defaultEndDate: phase.endDate,
                            doQueryFlag: 1,
                          })
                        }
                      />
                    );
                  })}
                </div>
              ) : null}
              {/* 等第制（GPAFormRow；studentMain.js:215-235：單表單 showFillForm／多表單 modal） */}
              {config.trainingColumns.includes('等第制') ? (
                <div data-ui-id={`${pageId}-gpaform-row`} className="flex">
                  {dto.phaseList.map((phase, index) => (
                    <PhaseBox
                      key={phase.phaseTargetId}
                      pageId={pageId}
                      kind="gpa"
                      index={index}
                      target={{
                        completePercent: gpaCompletePercent(phase),
                        totalQuantity: phase.gpaForms.length,
                        displayText: gpaDisplayText(phase),
                      }}
                      clickable={phase.gpaForms.some((form) => form.isDone)}
                      onClick={() => {
                        if (phase.gpaForms.length === 1) {
                          const form = phase.gpaForms[0];
                          if (form === undefined) return;
                          // 第三參數重複帶 formTemplateId＝legacy quirk 照實（studentMain.js:259）。
                          showFillForm(form.formKey, 'PhaseTarget', form.phaseTargetId, form.formTargetId);
                          return;
                        }
                        setGpaOpen(phase.gpaForms); // showGpaFormsModal
                      }}
                    />
                  ))}
                </div>
              ) : null}
              {/* EPA／EPAs（EPAFormRow；studentMain.js:237-244 click → showEpaFormsModal） */}
              {config.trainingColumns.some((column) => column === 'EPA' || column === 'EPAs') ? (
                <div data-ui-id={`${pageId}-epaform-row`} className="flex">
                  {dto.phaseList.map((phase, index) => (
                    <PhaseBox
                      key={phase.phaseTargetId}
                      pageId={pageId}
                      kind="epa"
                      index={index}
                      target={phase.epaForm}
                      clickable={phase.epaForms.length > 0 && phase.epaForm.completePercent > 0}
                      onClick={() => setEpaOpen(phase.epaForms)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </ContentPanel>
      ) : null}

      {/* teacher 變體：studentsRow（學員資訊面板；studentPanel_tmpl:303-365＋renderStudentPanel
          teacherMain.js:237-250：prepend＋同學員舊面板移除；panel 内點擊 :120-235） */}
      <div data-ui-id={`${pageId}-students-row`}>
        {openStudentPanels.map((panel) => (
          <ContentPanel
            key={panel.planInst.studentId}
            uiId={`${pageId}-student-panel-${kebab(panel.planInst.studentId)}`}
            title={`學員資訊（${panel.studentName}）`}
            actions={
              <button
                type="button"
                data-ui-id={`${pageId}-student-panel-${kebab(panel.planInst.studentId)}-close`}
                title="關閉（closeStudentPanel）"
                onClick={() =>
                  setOpenStudentPanels((current) =>
                    current.filter((candidate) => candidate.planInst.studentId !== panel.planInst.studentId),
                  )
                }
              >
                ×
              </button>
            }
          >
            <div className="space-y-2">
              {/* 成績燈號（legacy 是照片 click tooltipster——雛形直接常駐呈現，D19-x 註記；
                  dentisit 面板（dentisitStudentPanel_tmpl:367-420）無燈號區） */}
              {config.family === 'ugy' ? <GradeLights pageId={pageId} detail={panel} /> : null}
              {panel.coreSkillDTO !== undefined ? (
                <div data-ui-id={`${pageId}-panel-coreskill-row`} className="flex gap-1 overflow-x-auto">
                  {panel.coreSkillDTO.deptCoreSkills.map((deptCoreSkill, index) => (
                    <PhaseBox
                      key={deptCoreSkill.expansion1}
                      pageId={`${pageId}-panel-${kebab(panel.planInst.studentId)}`}
                      kind="coreSkill"
                      index={index}
                      target={{
                        completePercent: deptCoreSkill.completePercent,
                        totalQuantity: deptCoreSkill.totalQuantity,
                        displayText: `${deptCoreSkill.displayText}<br>${deptCoreSkill.completedQuantity}/${deptCoreSkill.totalQuantity}`,
                      }}
                      clickable
                      onClick={() => {
                        if (panel.fifthPlanTargetIdList.length === 0) {
                          // teacherMain.js:170 showAlertDialog('此學員並無五年級計畫！')。
                          void dialogs.alert('此學員並無五年級計畫！');
                          return;
                        }
                        setCoreskillOpen(true);
                      }}
                    />
                  ))}
                </div>
              ) : null}
              <div>
                {/* 標題列：ugy＝5 欄（studentPanel_tmpl:337-341）／dentisit＝3 欄
                    （dentisitStudentPanel_tmpl:392-394）／ma＝4 欄（ma_studentPanel_tmpl:441-444，
                    階段成績/EPAs 取代課程表單）；dentisit/ma 的等第制/EPA 標題不可點 */}
                <div className="flex text-center text-[13px] font-bold">
                  <div className="flex-1 p-1">訓練階段</div>
                  <div className="flex-1 p-1">計畫表單</div>
                  {config.family === 'ma' ? (
                    <div className="flex-1 p-1">階段成績</div>
                  ) : (
                    <div className="flex-1 p-1">課程表單</div>
                  )}
                  {config.family === 'ugy' ? (
                    <div
                      className="flex-1 cursor-pointer p-1"
                      data-ui-id={`${pageId}-gpa-grade-title-${kebab(panel.planInst.studentId)}`}
                      onClick={() => setGpaGradeOpen(panel)}
                    >
                      等第制
                    </div>
                  ) : null}
                  {config.family === 'ugy' || config.family === 'ma' ? (
                    <div
                      className={`flex-1 p-1 ${config.family === 'ugy' ? 'cursor-pointer' : ''}`}
                      data-ui-id={`${pageId}-epa-grade-title-${kebab(panel.planInst.studentId)}`}
                      onClick={config.family === 'ugy' ? () => setEpaGradeOpen(panel) : undefined}
                    >
                      {config.family === 'ma' ? 'EPAs' : 'EPA'}
                    </div>
                  ) : null}
                </div>
                <div className="flex overflow-x-auto">
                  <div className="flex">
                    <ul className="flex list-none p-0">
                      {panel.phaseList.map((phase, index) => (
                        <PhaseItem
                          key={phase.phaseTargetId}
                          pageId={`${pageId}-panel-${kebab(panel.planInst.studentId)}`}
                          index={index}
                          phase={phase}
                          displayName={config.family === 'ugy' ? undefined : tradeText(phase.phaseTargetName)}
                        />
                      ))}
                    </ul>
                  </div>
                  <div className="flex">
                    {panel.phaseList.map((phase, index) => (
                      <PhaseBox
                        key={phase.phaseTargetId}
                        pageId={`${pageId}-panel-${kebab(panel.planInst.studentId)}`}
                        kind="planForm"
                        index={index}
                        target={phase.planForm}
                        clickable={phase.planForm.totalQuantity > 0}
                        onClick={() => {
                          // MA：planForm 點擊 → 計畫表單 modal（medicalAffairs/teacherMain.js:118-123）；
                          // zwy/dentisit：planFormList 導航。
                          if (config.family === 'ma') {
                            setPlanFormsOpen(phase.planForms ?? []);
                            return;
                          }
                          openUrl('/form/planFormList', {
                            defaultAnnualId: panel.planInst.annualId,
                            defaultOrganizationId: panel.planInst.organizationId,
                            defaultJobTypeId: panel.planInst.jobTypeId,
                            defaultPlanTargetId: panel.planInst.planTargetId,
                            defaultPhaseDeptId: phase.phaseDeptId,
                            doQueryFlag: 1,
                            defaultStudentId: panel.planInst.studentId,
                            defaultStudentName: panel.studentName,
                            // dentisit 版（SubPhase）加帶起訖＋defaultPhaseType（dentisit/teacherMain.js:144-146）。
                            ...(config.family === 'dentisit'
                              ? {
                                  defaultStartDate: phase.startDate,
                                  defaultEndDate: phase.endDate,
                                  defaultPhaseType: 'SubPhase',
                                }
                              : {}),
                          });
                        }}
                      />
                    ))}
                  </div>
                  {/* 課程表單（zwy/dentisit）；MA 無此欄（ma_studentPanel_tmpl:441-444） */}
                  {config.family !== 'ma' ? (
                  <div className="flex">
                    {panel.phaseList.map((phase, index) => (
                      <PhaseBox
                        key={phase.phaseTargetId}
                        pageId={`${pageId}-panel-${kebab(panel.planInst.studentId)}`}
                        kind="classForm"
                        index={index}
                        target={phase.classForm}
                        clickable={phase.classForm.totalQuantity > 0}
                        onClick={() =>
                          openUrl('/form/classFormList', {
                            defaultOrganizationId: panel.planInst.organizationId,
                            defaultJobTypeId: panel.planInst.jobTypeId,
                            defaultDeptId: phase.phaseDeptId,
                            defaultStartDate: phase.startDate,
                            defaultEndDate: phase.endDate,
                            doQueryFlag: 1,
                            defaultStudentId: panel.planInst.studentId,
                            defaultStudentName: panel.studentName,
                            // deptExtraOptions（teacherMain.js:143-146：JSON 字串由 URL 編碼帶出，
                            // 線上格式＝encodeURIComponent 後同形）。
                            deptExtraOptions: JSON.stringify([{ id: phase.phaseDeptId, name: phase.phaseDeptName }]),
                            // dentisit 版加 defaultPhaseType（dentisit/teacherMain.js:169）。
                            ...(config.family === 'dentisit' ? { defaultPhaseType: 'SubPhase' } : {}),
                          })
                        }
                      />
                    ))}
                  </div>
                  ) : null}
                  {/* 階段成績（MA：phaseScoreRow click → classFormList；medicalAffairs/teacherMain.js:127-146
                      鏡像 zwy courseForm 參數） */}
                  {config.family === 'ma' ? (
                  <div className="flex">
                    {panel.phaseList.map((phase, index) => (
                      <PhaseBox
                        key={phase.phaseTargetId}
                        pageId={`${pageId}-panel-${kebab(panel.planInst.studentId)}`}
                        kind="phaseScore"
                        index={index}
                        target={{
                          completePercent: (phase.phaseScore?.score ?? '-') === '-' ? 0 : 100,
                          totalQuantity: (phase.phaseScore?.score ?? '-') === '-' ? 0 : 1,
                          displayText: phase.phaseScore?.score ?? '-',
                        }}
                        clickable={(phase.phaseScore?.score ?? '-') !== '-'}
                        onClick={() =>
                          openUrl('/form/classFormList', {
                            defaultOrganizationId: panel.planInst.organizationId,
                            defaultJobTypeId: panel.planInst.jobTypeId,
                            defaultDeptId: phase.phaseDeptId,
                            defaultStartDate: phase.startDate,
                            defaultEndDate: phase.endDate,
                            doQueryFlag: 1,
                            defaultStudentId: panel.planInst.studentId,
                            defaultStudentName: panel.studentName,
                          })
                        }
                      />
                    ))}
                  </div>
                  ) : null}
                  {config.family === 'ugy' ? (
                  <div className="flex">
                    {panel.phaseList.map((phase, index) => (
                      <PhaseBox
                        key={phase.phaseTargetId}
                        pageId={`${pageId}-panel-${kebab(panel.planInst.studentId)}`}
                        kind="gpa"
                        index={index}
                        target={{
                          completePercent: gpaCompletePercent(phase),
                          totalQuantity: phase.gpaForms.length,
                          displayText: gpaDisplayText(phase),
                        }}
                        clickable={phase.gpaForms.some((form) => form.isDone)}
                        onClick={() => {
                          if (phase.gpaForms.length === 1) {
                            const form = phase.gpaForms[0];
                            if (form === undefined) return;
                            showFillForm(form.formKey, 'PhaseTarget', form.phaseTargetId, form.formTargetId);
                            return;
                          }
                          setGpaOpen(phase.gpaForms);
                        }}
                      />
                    ))}
                  </div>
                  ) : null}
                  {config.family === 'ugy' ? (
                  <div className="flex">
                    {panel.phaseList.map((phase, index) => (
                      <PhaseBox
                        key={phase.phaseTargetId}
                        pageId={`${pageId}-panel-${kebab(panel.planInst.studentId)}`}
                        kind="epa"
                        index={index}
                        target={phase.epaForm}
                        clickable={phase.epaForms.length > 0 && phase.epaForm.completePercent > 0}
                        onClick={() => setEpaOpen(phase.epaForms)}
                      />
                    ))}
                  </div>
                  ) : null}
                </div>
              </div>
            </div>
          </ContentPanel>
        ))}
      </div>

      {/* 行事曆 modal（modal/calendar.jsp：calendarBox click → #calendarModal；月曆＝LightCalendar） */}
      <ModalFrame
        uiId={`${pageId}-calendar-modal`}
        open={calendarOpen}
        title="行事曆"
        onClose={() => setCalendarOpen(false)}
        size="lg"
      >
        <LightCalendar uiId={`${pageId}-calendar`} events={data.calendarEvents} onEventClick={onCalendarEventClick} />
      </ModalFrame>

      {/* 公告內容 modal（announcementModal；modal/announce.jsp showAnnounce） */}
      <AnnounceModal
        pageId={pageId}
        open={announceId !== null}
        detail={announceId === null ? null : (data.announcementDetails[announceId] ?? null)}
        onClose={() => setAnnounceId(null)}
      />

      {/* 待填表單 modal（todoFormModal；modal/todoForm.jsp） */}
      <TodoFormModal
        pageId={pageId}
        open={todoOpen}
        todoForms={data.todoForms}
        onClose={() => setTodoOpen(false)}
        onOpenForm={openTodoForm}
      />

      {/* 臨床核心技能表單 modal（coreSkillFormModal） */}
      <CoreSkillFormsModal
        pageId={pageId}
        open={coreskillOpen}
        forms={data.coreSkillForms}
        onClose={() => setCoreskillOpen(false)}
        onOpenForm={(form) => showFillForm(form.formKey, 'PhaseTarget', form.phaseTargetId, form.formTargetId)}
      />

      {/* 等第制表單 modal（gpaFormModal；footer「Close」照實） */}
      <GpaFormsModal
        pageId={pageId}
        open={gpaOpen !== null}
        forms={(gpaOpen ?? []).map((form) => ({
          formTemplateName: form.formTemplateName,
          displayGrade: form.displayGrade,
          startDate: form.startDate,
          dueDate: form.dueDate,
          flowList: form.flowList ?? [],
        }))}
        onClose={() => setGpaOpen(null)}
        onOpenForm={(form: GpaFormsModalProps['forms'][number]) =>
          showFillForm(
            String((form as { formKey?: string }).formKey ?? 'gpaForm'),
            'PhaseTarget',
            String((form as { phaseTargetId?: string }).phaseTargetId ?? '0'),
            String((form as { formTargetId?: string }).formTargetId ?? '0'),
          )
        }
      />

      {/* EPA表單 modal（epaFormModal；MA 版＝epaFormsForMA 欄位） */}
      <EpaFormsModal
        pageId={pageId}
        open={epaOpen !== null}
        maColumns={config.family === 'ma'}
        forms={(epaOpen ?? []).map((form) => ({
          formTemplateName: form.formTemplateName,
          displayGrade: form.displayGrade ?? '',
          startDate: form.startDate,
          dueDate: form.dueDate,
          flowList: form.flowList ?? [],
          isDone: form.isDone,
        }))}
        onClose={() => setEpaOpen(null)}
        onOpenForm={(form: Parameters<typeof EpaFormsModal>[0]['forms'][number]) =>
          showFillForm(
            String((form as { formKey?: string }).formKey ?? 'epaForm'),
            'PhaseTarget',
            String((form as { phaseTargetId?: string }).phaseTargetId ?? '0'),
            String((form as { formTargetId?: string }).formTargetId ?? '0'),
          )
        }
      />

      {/* 計畫表單 modal（MA 版 planFormsForMA；planForm 盒點擊） */}
      <PlanFormsModal
        pageId={pageId}
        open={planFormsOpen !== null}
        forms={planFormsOpen ?? []}
        onClose={() => setPlanFormsOpen(null)}
        onOpenForm={(form) => showFillForm(form.formKey, 'PhaseTarget', form.phaseTargetId, form.formTargetId)}
      />

      {/* 個人行程 modal（addCalendarModal；eventClick schedule → 修改個人行程）。
          掛 open 條件渲染：ScheduleModal 內部 useState 以 schedule 初值，重開時必須重 mounting。 */}
      {scheduleModalOpen ? (
        <ScheduleModal
        pageId={pageId}
        open={scheduleModalOpen}
        schedule={scheduleEditing}
        onClose={() => setScheduleModalOpen(false)}
        onSave={(values) => {
          setData(
            updateDashboardData(
              scenario,
              (current) => {
                const exists = current.mySchedules.some((schedule) => schedule.id === values.id);
                return {
                  ...current,
                  mySchedules: exists
                    ? current.mySchedules.map((schedule) => (schedule.id === values.id ? values : schedule))
                    : [...current.mySchedules, values],
                };
              },
              now,
            ),
          );
          setScheduleEditing(values); // saveSchedule 成功後標題變「修改個人行程」（:223）
          void dialogs.alert('存檔成功【雛形值待實機校準】'); // showInfoDialog(response.desc)
        }}
        onDelete={() => {
          if (scheduleEditing === null) return;
          setData(
            updateDashboardData(
              scenario,
              (current) => ({
                ...current,
                mySchedules: current.mySchedules.filter((schedule) => schedule.id !== scheduleEditing.id),
              }),
              now,
            ),
          );
          setScheduleModalOpen(false);
          void dialogs.alert('刪除成功【雛形值待實機校準】');
        }}
        onApprove={(isEnable) => {
          void dialogs.alert(isEnable === 1 ? '已接受【雛形值待實機校準】' : '已婉拒【雛形值待實機校準】');
        }}
      />
      ) : null}

      {/* 檢視課程 modal（courseModal＝批次 11 CourseViewModal；行事曆 class/meeting 點擊） */}
      {courseRow !== null ? (
        <CourseViewModal pageId={pageId} row={courseRow} onClose={() => setCourseRow(null)} />
      ) : null}

      {/* 檢視等第制成績分布 modal（S2；teacherMain.js:174-179 GPAGradeTitle click；
          報表種子直出——查詢鈕不重打（getStudentGPAFormReport 種子不分區間，D19-x）） */}
      {gpaGradeOpen !== null ? (
        <StudentGpaGradeModal
          pageId={pageId}
          open
          detail={gpaGradeOpen}
          report={data.gpaFormReport}
          onClose={() => setGpaGradeOpen(null)}
          onOpenForm={(row) => showFillForm(row.formKey, 'PhaseTarget', row.phaseTargetId, row.formTargetId)}
        />
      ) : null}

      {/* 檢視EPA成績分布 modal（S2；teacherMain.js:181-186 EPAGradeTitle click） */}
      {epaGradeOpen !== null ? (
        <StudentEpaGradeModal
          pageId={pageId}
          open
          detail={epaGradeOpen}
          report={data.epaFormReport}
          onClose={() => setEpaGradeOpen(null)}
          onOpenForm={(row) => showFillForm(row.formKey, 'PhaseTarget', row.phaseTargetId, row.formTargetId)}
        />
      ) : null}
    </div>
  );
}
